// Discrepancies.jsx — Admin review queue for broker vs journal mismatches
// Backed by GET /api/admin/discrepancies (+ /detect, /:id/apply, /:id/reject).
// Auto-refreshes every 30 seconds while the tab is mounted.

import React, { useState, useEffect, useCallback } from 'react';

const TYPE_LABEL = {
  corporate_action: 'Corporate Action',
  stale_journal_open: 'Stale OPEN Entry',
  unmatched_broker_position: 'Unmatched Broker Position',
};

const TYPE_COLOR = {
  corporate_action: { bg: '#1a2e3a', text: '#60a5fa', border: '#1e40af' },
  stale_journal_open: { bg: '#3a2e1a', text: '#fbbf24', border: '#92400e' },
  unmatched_broker_position: { bg: '#3a1a1a', text: '#f87171', border: '#991b1b' },
};

function fmt(v, decimals = 2) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return n.toFixed(decimals);
}

function ChangeRow({ field, change }) {
  return (
    <tr>
      <td style={{ padding: '4px 8px', color: '#9ca3af', fontFamily: 'monospace', fontSize: 11 }}>{field}</td>
      <td style={{ padding: '4px 8px', color: '#f87171', textAlign: 'right' }}>{fmt(change.old, 6)}</td>
      <td style={{ padding: '4px 8px', color: '#9ca3af', textAlign: 'center' }}>→</td>
      <td style={{ padding: '4px 8px', color: '#4ade80', textAlign: 'right' }}>{fmt(change.new, 6)}</td>
    </tr>
  );
}

function DiscrepancyCard({ item, onApply, onReject, onSuggestFix, busy }) {
  const [expanded, setExpanded] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const color = TYPE_COLOR[item.type] || TYPE_COLOR.unmatched_broker_position;
  const canApply = item.status === 'PENDING' && item.proposed_changes && Object.keys(item.proposed_changes).length > 0;
  const aiSuggestable = item.status === 'PENDING' && !canApply;

  const runSuggest = async () => {
    setAiLoading(true); setAiError(null); setAiResult(null);
    try {
      const result = await onSuggestFix(item.id);
      setAiResult(result);
      if (!result?.success) setAiError(result?.reasoning || 'No suggestion returned');
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div style={{
      border: `1px solid ${color.border}`, borderRadius: 8, marginBottom: 16,
      background: '#1a1d24', overflow: 'hidden',
    }}>
      <div style={{
        padding: '10px 14px', background: color.bg, display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: expanded ? `1px solid ${color.border}` : 'none', cursor: 'pointer',
      }} onClick={() => setExpanded(!expanded)}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
          background: color.border, color: '#fff',
        }}>{TYPE_LABEL[item.type] || item.type}</span>
        <span style={{ fontSize: 13, color: '#f9fafb', fontWeight: 600 }}>
          {item.account} · {item.symbol}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>
          #{item.id} · detected {item.detected_at} · {item.status}
        </span>
        <span style={{ color: '#9ca3af', fontSize: 12 }}>{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: 12, color: '#d1d5db', marginBottom: 12, lineHeight: 1.5 }}>
            {item.description}
          </div>

          {/* Side-by-side broker + journal snapshots */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div style={{ background: '#0f1117', border: '1px solid #374151', borderRadius: 6, padding: 10 }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Broker</div>
              {item.broker_snapshot ? (
                <pre style={{ margin: 0, fontSize: 11, color: '#d1d5db', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {JSON.stringify(item.broker_snapshot, null, 2)}
                </pre>
              ) : (
                <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>none</div>
              )}
            </div>
            <div style={{ background: '#0f1117', border: '1px solid #374151', borderRadius: 6, padding: 10 }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Journal</div>
              {item.journal_snapshot ? (
                <pre style={{ margin: 0, fontSize: 11, color: '#d1d5db', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {JSON.stringify(item.journal_snapshot, null, 2)}
                </pre>
              ) : (
                <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>none</div>
              )}
            </div>
          </div>

          {/* Proposed changes */}
          {item.proposed_changes && Object.keys(item.proposed_changes).length > 0 && (
            <div style={{ background: '#0f1117', border: '1px solid #374151', borderRadius: 6, padding: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Proposed changes to {item.proposed_table} #{item.proposed_row_id}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: '#9ca3af', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px' }}>Field</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>Old</th>
                    <th style={{ width: 30 }}></th>
                    <th style={{ textAlign: 'right', padding: '4px 8px' }}>New</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(item.proposed_changes).map(([field, change]) => (
                    <ChangeRow key={field} field={field} change={change} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Status-specific actions */}
          {item.status === 'PENDING' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {canApply && (
                <button
                  disabled={busy}
                  onClick={() => onApply(item.id)}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 600,
                    background: '#15803d', color: '#fff', border: 'none', borderRadius: 4,
                    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1,
                  }}
                >Apply</button>
              )}
              {!showRejectInput && (
                <button
                  disabled={busy}
                  onClick={() => setShowRejectInput(true)}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 600,
                    background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: 4,
                    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1,
                  }}
                >Reject</button>
              )}
              {showRejectInput && (
                <>
                  <input
                    type="text"
                    placeholder="Reason (optional, 500 chars max)"
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    maxLength={500}
                    style={{
                      flex: 1, minWidth: 200, padding: '6px 10px', fontSize: 12,
                      background: '#0f1117', color: '#f9fafb', border: '1px solid #374151', borderRadius: 4,
                    }}
                  />
                  <button
                    disabled={busy}
                    onClick={() => { onReject(item.id, rejectNote); setShowRejectInput(false); setRejectNote(''); }}
                    style={{
                      padding: '6px 14px', fontSize: 12, fontWeight: 600,
                      background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
                    }}
                  >Confirm</button>
                  <button
                    onClick={() => { setShowRejectInput(false); setRejectNote(''); }}
                    style={{
                      padding: '6px 14px', fontSize: 12,
                      background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: 4, cursor: 'pointer',
                    }}
                  >Cancel</button>
                </>
              )}
              {aiSuggestable && (
                <button
                  disabled={busy || aiLoading}
                  onClick={runSuggest}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 600,
                    background: '#5b21b6', color: '#fff', border: 'none', borderRadius: 4,
                    cursor: (busy || aiLoading) ? 'not-allowed' : 'pointer',
                    opacity: (busy || aiLoading) ? 0.5 : 1,
                  }}
                  title="Ask Claude to propose a fix. Reviewed + validated server-side; you still click Apply to commit."
                >{aiLoading ? 'Thinking…' : 'Suggest fix (AI)'}</button>
              )}
              {!canApply && !aiSuggestable && (
                <span style={{ fontSize: 11, color: '#fbbf24', fontStyle: 'italic' }}>
                  No auto-applicable proposal — manual action required (use db-correct.js or close in app).
                </span>
              )}
            </div>
          )}

          {/* AI suggestion result — appears after Suggest-fix click */}
          {aiResult && (
            <div style={{
              marginTop: 12, padding: 10, borderRadius: 6,
              background: aiResult.should_apply ? '#1a2a3a' : '#2a2410',
              border: `1px solid ${aiResult.should_apply ? '#1e40af' : '#92400e'}`,
            }}>
              <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                AI proposal · model {aiResult.model || '?'} ·
                confidence {(aiResult.confidence ?? 0).toFixed(2)} ·
                {aiResult.usage ? ` ${aiResult.usage.input_tokens || 0}+${aiResult.usage.output_tokens || 0} tokens` : ''}
              </div>
              {aiResult.reasoning && (
                <div style={{ fontSize: 12, color: '#d1d5db', marginBottom: 8, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {aiResult.reasoning}
                </div>
              )}
              {aiResult.warnings && aiResult.warnings.length > 0 && (
                <ul style={{ fontSize: 11, color: '#fbbf24', margin: '4px 0 8px 16px', padding: 0 }}>
                  {aiResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              {aiResult.proposed_changes && Object.keys(aiResult.proposed_changes).length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: '#9ca3af', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Field</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Old</th>
                      <th style={{ width: 30 }}></th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(aiResult.proposed_changes).map(([field, change]) => (
                      <ChangeRow key={field} field={field} change={change} />
                    ))}
                  </tbody>
                </table>
              )}
              {aiResult.should_apply && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#86efac' }}>
                  ✓ Proposal saved. Reload the list (or wait 30s for auto-refresh) — an Apply button will appear on this card.
                </div>
              )}
              {!aiResult.should_apply && aiResult.success && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#fbbf24' }}>
                  ⚠ Confidence below 0.85 — advisory only, not saved. Use the diagnosis to guide a manual db-correct.js fix.
                </div>
              )}
            </div>
          )}
          {aiError && !aiResult?.success && (
            <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: '#3a1a1a', border: '1px solid #991b1b', color: '#fca5a5', fontSize: 12 }}>
              AI error: {aiError}
            </div>
          )}

          {item.status !== 'PENDING' && (
            <div style={{ fontSize: 11, color: '#9ca3af' }}>
              {item.status} by {item.reviewed_by} at {item.reviewed_at}
              {item.applied_activity_log_id && ` · activity_logs #${item.applied_activity_log_id}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Discrepancies({ sessionId }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [lastEod, setLastEod] = useState(null);

  const headers = useCallback(() => ({
    'x-session-id': sessionId || '',
    'Content-Type': 'application/json',
  }), [sessionId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/discrepancies?status=${status}&limit=200`, { headers: headers() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.items || []);
      setLastEod(data.last_eod_verification || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [status, headers]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s while mounted
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const runDetect = async () => {
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await fetch('/api/admin/discrepancies/detect', { method: 'POST', headers: headers() });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.json()).error || ''}`);
      const data = await r.json();
      setInfo(`Detection done — ${data.inserted} new, ${data.skipped} already-queued, ${data.total_pending} total pending${data.message ? '. ' + data.message : ''}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async (id) => {
    if (!window.confirm(`Apply discrepancy #${id}? This writes the proposed UPDATE + an activity_logs entry. Cannot be undone via the UI.`)) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await fetch(`/api/admin/discrepancies/${id}/apply`, { method: 'POST', headers: headers() });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.json()).error || ''}`);
      const data = await r.json();
      setInfo(`Applied #${id} — activity_logs row ${data.activity_log_id}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (id, note) => {
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await fetch(`/api/admin/discrepancies/${id}/reject`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ note: note || null }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${(await r.json()).error || ''}`);
      setInfo(`Rejected #${id}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Ask Claude for a fix proposal. Returns the result so the card can render
  // it inline. If confidence >= 0.85, server saves proposed_changes to the
  // discrepancy row — the next list refresh will surface an Apply button.
  const handleSuggestFix = async (id) => {
    setError(null);
    const r = await fetch(`/api/admin/discrepancies/${id}/suggest-fix`, {
      method: 'POST', headers: headers(),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${data.error || ''}`);
    // If high-confidence proposal was saved, refresh list so Apply button appears
    if (data.should_apply) {
      await load();
    }
    return data;
  };

  return (
    <div style={{ padding: 16, color: '#f9fafb', background: '#0f1117', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Discrepancies</h2>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>
          Broker positions vs trading journal — auto-detected mismatches awaiting review.
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Status filter */}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{
              padding: '6px 10px', fontSize: 12, background: '#1a1d24', color: '#f9fafb',
              border: '1px solid #374151', borderRadius: 4,
            }}
          >
            <option value="PENDING">Pending</option>
            <option value="APPLIED">Applied</option>
            <option value="REJECTED">Rejected</option>
            <option value="ALL">All</option>
          </select>
          {/* "Run detection now" removed (Phase 1, 30-May): manual runs during the day
              produce noise because journal updates only at EOD, so OPEN journal entries
              never match live broker positions during market hours. Detection now runs
              only at 9:10 AM + 7:00 PM IST cron schedules. */}
          <button
            disabled={loading}
            onClick={load}
            style={{
              padding: '6px 14px', fontSize: 12,
              background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >Refresh</button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{ padding: 10, background: '#3a1a1a', border: '1px solid #991b1b', color: '#fca5a5', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
          Error: {error}
        </div>
      )}
      {info && (
        <div style={{ padding: 10, background: '#1a3a1a', border: '1px solid #166534', color: '#86efac', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
          {info}
        </div>
      )}

      {/* Last EOD verification — green banner if all clear, yellow otherwise */}
      {lastEod && (() => {
        const dates = lastEod.dates_checked || (lastEod.end_date ? [lastEod.end_date] : []);
        const dateLabel = dates.length > 1
          ? `${dates[dates.length - 1]} → ${dates[0]}`
          : (dates[0] || lastEod.end_date || lastEod.date || '');
        return (
          <div style={{
            padding: 12, marginBottom: 16, borderRadius: 6, fontSize: 12,
            background: lastEod.all_clear ? '#0d2818' : '#2a2410',
            border: lastEod.all_clear ? '1px solid #166534' : '1px solid #92400e',
            color: lastEod.all_clear ? '#86efac' : '#fbbf24',
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 14 }}>{lastEod.all_clear ? '✓' : '⚠'}</span>
            <span style={{ fontWeight: 600 }}>
              {lastEod.all_clear
                ? `All ${lastEod.tradebook_verification?.verified_ok || 0} journal entries verified against broker tradebook (${dateLabel}).`
                : `Last EOD verification (${dateLabel}) flagged issues.`}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.85 }}>
              ran {lastEod.ran_at} ·
              tradebook: {lastEod.tradebook_verification?.verified_ok || 0} ok,
              {lastEod.tradebook_verification?.inserted || 0} flagged,
              {lastEod.tradebook_verification?.skipped_no_data || 0} skipped (no broker data) ·
              positions: {lastEod.position_detection?.inserted || 0} new,
              {lastEod.position_detection?.total_pending || 0} pending
            </span>
          </div>
        );
      })()}
      {!lastEod && (
        <div style={{
          padding: 12, marginBottom: 16, borderRadius: 6, fontSize: 12,
          background: '#1a1d24', border: '1px solid #374151', color: '#9ca3af',
        }}>
          No EOD verification has run yet. Will run automatically at 4:15 PM IST. You can also click "Run detection now" above.
        </div>
      )}

      {/* Items */}
      {loading && items.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          No {status.toLowerCase()} discrepancies.
        </div>
      )}
      {items.map(item => (
        <DiscrepancyCard
          key={item.id}
          item={item}
          onApply={handleApply}
          onReject={handleReject}
          onSuggestFix={handleSuggestFix}
          busy={busy}
        />
      ))}
    </div>
  );
}
